import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Header } from './Gym';
import { useRehab, TEST_TYPES, testLabel, fmtDay, addDays } from './rehab';

/**
 * Rehab charts: pain, strength, pain at a fixed load and swelling over time.
 * Every chart shades the background by block (named) and marks the blocks'
 * dated notes, over one range picked at the top (4 weeks, 3 months, all).
 *
 * A missing day is a gap, never a zero: only logged days are points, and a
 * line breaks across a stretch with nothing logged. Strength is two charts
 * on one time axis, force by hand and then the ratio, rather than one chart
 * with two scales.
 *
 * Colours are the categorical slots in order, dark steps, fixed per series so
 * hiding one never repaints the rest. Left is slot 1, right slot 2, on every
 * chart. Text stays in text colours; a swatch beside it carries the identity.
 */

export const PAIN_SERIES = [
  { key: 'rest_pain', label: 'Finger pain', colour: '#3987e5' },
  { key: 'palm', label: 'Palm', colour: '#d95926' },
  { key: 'post_pain', label: 'After climbing', colour: '#199e70' },
  { key: 'stiffness', label: 'Stiffness', colour: '#c98500' },
  { key: 'provocative', label: 'Provocative test', colour: '#d55181' },
];
const HANDS = [{ key: 'left', label: 'Left', colour: '#3987e5' }, { key: 'right', label: 'Right (injured)', colour: '#d95926' }];
const RATIO_COLOUR = '#9085e9';
const KNUCKLE_COLOUR = '#199e70';

const T = (date) => Date.parse(`${date}T00:00:00Z`);
const num = (v) => (v == null ? '' : String(Math.round(v * 10) / 10));
const short = (t) => fmtDay(new Date(t).toISOString().slice(0, 10), { day: 'numeric', month: 'short' });

const RANGES = [['4w', '4 weeks'], ['3m', '3 months'], ['all', 'All']];

export default function RehabCharts() {
  const { data } = useRehab();
  const today = data.analysis.today;
  const [range, setRange] = useState('4w');
  const [avg, setAvg] = useState(true);
  const [hidden, setHidden] = useState({});
  const [testType, setTestType] = useState('mono|half');

  const first = [data.days[0]?.date, data.sessions[0]?.date, data.tests[0]?.date, data.blocks[0]?.start_date].filter(Boolean).sort()[0] || addDays(today, -27);
  const from = range === '4w' ? addDays(today, -27) : range === '3m' ? addDays(today, -90) : (first < today ? first : addDays(today, -27));
  const domain = [T(from), T(today)];

  const blocks = data.blocks.map(b => ({ name: b.name, t0: T(b.start_date), t1: T(addDays(b.start_date, (b.length_days || 28) - 1)) }));
  const markers = data.blocks.flatMap(b => (b.markers || []).map(m => ({ t: T(m.date), text: m.text })));
  const frame = { domain, blocks, markers };

  const painSeries = PAIN_SERIES.filter(s => !hidden[s.key]).map(s => {
    const pts = data.analysis.pain[s.key] || [];
    return {
      ...s,
      points: pts.map(p => ({ t: T(p.date), v: avg ? p.avg : p.value, raw: p.value, avg: p.avg })),
      faded: avg ? pts.map(p => ({ t: T(p.date), v: p.value })) : null,
    };
  });

  const [test, grip] = testType.split('|');
  const ratios = data.analysis.ratios.filter(r => r.test === test && r.grip === grip);
  const forceSeries = HANDS.map(h => ({ ...h, points: ratios.filter(r => r[`${h.key}_kg`] != null).map(r => ({ t: T(r.date), v: r[`${h.key}_kg`], pain: r[`${h.key}_pain`] })) }));
  const ratioSeries = [{ key: 'ratio', label: 'Right ÷ left', colour: RATIO_COLOUR, points: ratios.filter(r => r.ratio != null).map(r => ({ t: T(r.date), v: r.ratio })) }];

  const fixed = data.tests.filter(t => t.fixed);
  const fixedSeries = HANDS.map(h => ({ ...h, points: fixed.filter(t => t.fixed[`${h.key}_pain`] != null).map(t => ({ t: T(t.date), v: t.fixed[`${h.key}_pain`], note: `${num(t.fixed.load_kg)} kg, ${testLabel(t.fixed.test, t.fixed.grip).toLowerCase()}` })) }));
  const knuckleSeries = [{ key: 'knuckle', label: 'Knuckle', colour: KNUCKLE_COLOUR, points: data.analysis.knuckle.map(p => ({ t: T(p.date), v: p.value })) }];

  return (
    <div className="pt-0 pb-6">
      <Header title="Rehab charts" back="/gym/rehab" />
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Pills options={RANGES} value={range} onChange={setRange} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel
          title="Pain"
          hint={avg ? '7-day rolling average over the days logged; each day faded behind it.' : 'Each day as logged.'}
          right={<Pills small options={[['avg', '7-day avg'], ['raw', 'Daily']]} value={avg ? 'avg' : 'raw'} onChange={v => setAvg(v === 'avg')} />}
        >
          <div className="flex flex-wrap gap-1.5 mb-2">
            {PAIN_SERIES.map(s => (
              <button key={s.key} type="button" aria-pressed={!hidden[s.key]} onClick={() => setHidden(h => ({ ...h, [s.key]: !h[s.key] }))} className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${hidden[s.key] ? 'border-[#2a2a2a] text-gray-600' : 'border-[#3a3a3a] text-gray-200'}`}>
                <span className="w-3 h-0.5 rounded" style={{ background: hidden[s.key] ? '#3a3a3a' : s.colour }} />{s.label}
              </button>
            ))}
          </div>
          <TimeChart {...frame} series={painSeries} yFixed={[0, 10]} yTicks={[0, 2, 4, 6, 8, 10]} gapDays={7} unit="/10" empty="No daily logs in this range." />
        </Panel>

        <Panel
          title="Strength at target pain"
          hint="Max force for a 10 s hold at the target pain, from the weekly test."
          right={
            <select value={testType} onChange={e => setTestType(e.target.value)} className="bg-[#141414] border border-[#2a2a2a] rounded-lg px-2 py-1 text-xs text-white">
              {TEST_TYPES.map(t => <option key={`${t.test}|${t.grip}`} value={`${t.test}|${t.grip}`}>{t.label}</option>)}
            </select>
          }
        >
          <TimeChart {...frame} series={forceSeries} gapDays={28} unit=" kg" height={190} legend empty="No tests of this type in this range." />
          <div className="text-xs text-gray-400 mt-3 mb-1">Right as a % of left <span className="text-gray-600">(100% is level)</span></div>
          <TimeChart {...frame} series={ratioSeries} gapDays={28} unit="%" height={150} reference={100} floor={0} empty="A ratio needs both hands tested on the same day." />
        </Panel>

        <Panel title="Pain at a fixed load" hint="The same load each time; should come down while the force at target pain goes up.">
          <TimeChart {...frame} series={fixedSeries} yFixed={[0, 10]} yTicks={[0, 2, 4, 6, 8, 10]} gapDays={28} unit="/10" legend empty="No fixed-load tests in this range." />
        </Panel>

        <Panel title="Swelling" hint="Knuckle circumference.">
          <TimeChart {...frame} series={knuckleSeries} gapDays={7} unit=" mm" empty="No knuckle measurements in this range." />
        </Panel>
      </div>
    </div>
  );
}

function Pills({ options, value, onChange, small }) {
  return (
    <div className={`flex rounded-full border border-[#2a2a2a] overflow-hidden ${small ? 'text-xs' : 'text-sm'}`}>
      {options.map(([k, label]) => (
        <button key={k} type="button" onClick={() => onChange(k)} className={`${small ? 'px-2.5 py-1' : 'px-4 py-1.5'} ${value === k ? 'bg-[#1f1f1f] text-white' : 'text-gray-400'}`}>{label}</button>
      ))}
    </div>
  );
}

function Panel({ title, hint, right, children }) {
  return (
    <div className="rounded-2xl bg-[#141414] border border-[#232323] p-4 min-w-0">
      <div className="flex items-start gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="font-semibold">{title}</div>
          {hint && <div className="text-xs text-gray-500 mt-0.5">{hint}</div>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function useWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(340);
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(240, e.contentRect.width)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

function niceTicks(lo, hi, count = 4) {
  if (lo === hi) { lo -= 1; hi += 1; }
  const raw = (hi - lo) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map(m => m * mag).find(s => s >= raw) || raw;
  const ticks = [];
  for (let v = Math.floor(lo / step) * step; v <= hi + step * 0.001; v += step) ticks.push(Math.round(v * 100) / 100);
  if (ticks.at(-1) < hi) ticks.push(Math.round((ticks.at(-1) + step) * 100) / 100);
  return ticks;
}

function timeTicks(t0, t1, width) {
  const max = Math.max(2, Math.floor(width / 70));
  const days = (t1 - t0) / 864e5;
  const step = [1, 2, 7, 14, 30, 61, 91].find(s => days / s <= max) || 182;
  const out = [];
  for (let t = t1; t >= t0; t -= step * 864e5) out.unshift(t);
  return out;
}

/** Points split where nothing was logged for longer than gapDays, so a gap shows as a gap. */
function segments(points, gapDays) {
  const out = [];
  let cur = [];
  for (const p of points) {
    if (cur.length && p.t - cur.at(-1).t > gapDays * 864e5) { out.push(cur); cur = []; }
    cur.push(p);
  }
  if (cur.length) out.push(cur);
  return out;
}

const M = { top: 18, right: 40, bottom: 24, left: 34 };

/** A block's name cut to fit its band (about 5.5 px a character at 10 px), or nothing if it won't. */
function fitLabel(name, room) {
  const chars = Math.floor(room / 5.5);
  if (chars < 4) return '';
  return name.length <= chars ? name : `${name.slice(0, chars - 1)}…`;
}

/**
 * A time chart: lines per series over a fixed date range, blocks shaded
 * behind, block notes as markers along the top, crosshair and readout on
 * hover or touch.
 */
function TimeChart({ domain, blocks, markers, series, yFixed, yTicks: givenTicks, gapDays, unit, height = 210, legend, reference, floor, empty }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const [t0, t1] = domain;
  const inRange = (t) => t >= t0 - 864e5 / 2 && t <= t1 + 864e5 / 2;
  const shown = series.map(s => ({ ...s, points: s.points.filter(p => p.v != null && inRange(p.t)), faded: s.faded?.filter(p => p.v != null && inRange(p.t)) }));
  const values = shown.flatMap(s => [...s.points, ...(s.faded || [])].map(p => p.v));
  const has = shown.some(s => s.points.length);

  let yTicks = givenTicks;
  if (!yTicks) {
    const vs = reference != null ? [...values, reference] : values;
    const lo = vs.length ? Math.min(...vs) : 0;
    const hi = vs.length ? Math.max(...vs) : 1;
    const pad = Math.max(0.5, (hi - lo) * 0.15);
    yTicks = niceTicks(Math.max(floor ?? -Infinity, lo - pad), hi + pad);
  }
  const [y0, y1] = yFixed || [yTicks[0], yTicks.at(-1)];
  const iw = width - M.left - M.right;
  const ih = height - M.top - M.bottom;
  const span = Math.max(864e5, t1 - t0);
  const x = (t) => M.left + ((t - t0) / span) * iw;
  const y = (v) => M.top + ih - ((v - y0) / (y1 - y0)) * ih;

  const times = [...new Set([...shown.flatMap(s => s.points.map(p => p.t)), ...markers.filter(m => inRange(m.t)).map(m => m.t)])].sort((a, b) => a - b);
  function onMove(e) {
    if (!times.length) return;
    const box = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - box.left) / box.width) * width;
    setHover(times.reduce((best, t) => (Math.abs(x(t) - px) < Math.abs(x(best) - px) ? t : best), times[0]));
  }
  const at = hover == null ? [] : shown.map(s => ({ s, p: s.points.find(p => p.t === hover) })).filter(r => r.p);
  const notes = hover == null ? [] : markers.filter(m => m.t === hover);
  const visibleBlocks = blocks.filter(b => b.t1 >= t0 && b.t0 <= t1);

  return (
    <div ref={ref} className="relative">
      {legend && shown.length > 1 && (
        <div className="flex gap-4 text-xs text-gray-400 mb-1">
          {shown.map(s => <span key={s.key} className="flex items-center gap-1.5"><span className="w-3 h-0.5 rounded" style={{ background: s.colour }} />{s.label}</span>)}
        </div>
      )}
      <svg width={width} height={height} className="block touch-pan-y" onPointerMove={onMove} onPointerDown={onMove} onPointerLeave={() => setHover(null)} role="img" aria-label={`${series.map(s => s.label).join(', ')} over time`}>
        {visibleBlocks.map((b, i) => {
          const a = Math.max(M.left, x(b.t0 - 864e5 / 2));
          const z = Math.min(width - M.right, x(b.t1 + 864e5 / 2));
          return (
            <g key={i}>
              <rect x={a} y={M.top} width={Math.max(0, z - a)} height={ih} fill={i % 2 ? '#ffffff0a' : '#ffffff12'} />
              <line x1={a} x2={a} y1={M.top} y2={M.top + ih} stroke="#3a3a3a" strokeDasharray="2 3" />
              <text x={a + 4} y={M.top + 11} fontSize="10" fill="#8a8a8a">{fitLabel(b.name, z - a - 8)}</text>
            </g>
          );
        })}
        {yTicks.map(v => (
          <g key={v}>
            <line x1={M.left} x2={width - M.right} y1={y(v)} y2={y(v)} stroke="#262626" />
            <text x={M.left - 6} y={y(v) + 4} textAnchor="end" fontSize="10" fill="#8a8a8a">{v}</text>
          </g>
        ))}
        {reference != null && reference >= y0 && reference <= y1 && <line x1={M.left} x2={width - M.right} y1={y(reference)} y2={y(reference)} stroke="#6b6b6b" strokeDasharray="4 3" />}
        {timeTicks(t0, t1, iw).map(t => <text key={t} x={x(t)} y={height - 6} textAnchor="middle" fontSize="10" fill="#8a8a8a">{short(t)}</text>)}
        {markers.filter(m => inRange(m.t)).map((m, i) => (
          <g key={i}>
            <line x1={x(m.t)} x2={x(m.t)} y1={M.top} y2={M.top + ih} stroke="#5a5a5a" strokeWidth="1" strokeDasharray="1 3" />
            <path d={`M${x(m.t)},${M.top - 1} l-4,-6 h8 z`} fill="#bdbdbd" />
          </g>
        ))}
        {hover != null && <line x1={x(hover)} x2={x(hover)} y1={M.top} y2={M.top + ih} stroke="#4a4a4a" />}
        {shown.map(s => (
          <g key={s.key}>
            {s.faded?.map((p, i) => <circle key={`f${i}`} cx={x(p.t)} cy={y(p.v)} r="2.5" fill={s.colour} opacity="0.3" />)}
            {segments(s.points, gapDays).map((seg, i) => (seg.length > 1
              ? <polyline key={i} fill="none" stroke={s.colour} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" points={seg.map(p => `${x(p.t)},${y(p.v)}`).join(' ')} />
              : null))}
            {s.points.map((p, i) => ((!s.faded || p.t === hover || s.points.length < 12) ? <circle key={i} cx={x(p.t)} cy={y(p.v)} r={p.t === hover ? 5 : 4} fill={s.colour} stroke="#141414" strokeWidth="2" /> : null))}
            {s.points.length > 0 && shown.length <= 2 && <text x={x(s.points.at(-1).t) + 7} y={y(s.points.at(-1).v) + 4} fontSize="11" fill="#d4d4d4">{num(s.points.at(-1).v)}</text>}
          </g>
        ))}
        {!has && <text x={M.left + iw / 2} y={M.top + ih / 2} textAnchor="middle" fontSize="12" fill="#6b6b6b">{empty}</text>}
      </svg>
      {(at.length > 0 || notes.length > 0) && (
        <div className="pointer-events-none absolute top-4 z-10 rounded-lg bg-[#0d0d0d] border border-[#333] px-3 py-2 text-xs shadow-lg min-w-[140px] max-w-[220px]" style={x(hover) > width / 2 ? { right: width - x(hover) + 10 } : { left: x(hover) + 10 }}>
          <div className="text-gray-400 mb-1">{fmtDay(new Date(hover).toISOString().slice(0, 10))}</div>
          {at.map(({ s, p }) => (
            <div key={s.key} className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: s.colour }} />
              <span className="text-gray-300 truncate">{s.label}</span>
              <span className="ml-auto tabular-nums text-white whitespace-nowrap">{num(p.v)}{unit}{p.avg != null && p.raw != null && p.raw !== p.v ? <span className="text-gray-500"> ({p.raw})</span> : null}</span>
            </div>
          ))}
          {at.some(r => r.p.note) && <div className="text-gray-500 mt-1">{at.find(r => r.p.note).p.note}</div>}
          {at.some(r => r.p.pain != null) && <div className="text-gray-500 mt-1">{at.filter(r => r.p.pain != null).map(r => `${r.s.label.split(' ')[0]} at ${r.p.pain}/10`).join(', ')}</div>}
          {notes.map((n, i) => <div key={i} className="text-gray-200 mt-1">▾ {n.text}</div>)}
        </div>
      )}
    </div>
  );
}

/** The home screen's trend: 7-day averages of finger pain and palm over the last 4 weeks. */
export function Sparkline({ analysis, today }) {
  const [ref, width] = useWidth();
  const h = 56;
  const t0 = T(addDays(today, -27));
  const t1 = T(today);
  const lines = PAIN_SERIES.slice(0, 2).map(s => ({ ...s, pts: (analysis.pain[s.key] || []).filter(p => p.avg != null && T(p.date) >= t0).map(p => ({ t: T(p.date), v: p.avg })) }));
  const x = (t) => 2 + ((t - t0) / (t1 - t0)) * (width - 40);
  const y = (v) => h - 4 - (v / 10) * (h - 8);
  const latest = (s) => s.pts.at(-1)?.v;
  return (
    <div ref={ref}>
      <div className="flex gap-4 text-xs text-gray-400 mb-1">
        {lines.map(s => <span key={s.key} className="flex items-center gap-1.5"><span className="w-3 h-0.5 rounded" style={{ background: s.colour }} />{s.label} <span className="tabular-nums text-white">{latest(s) != null ? num(latest(s)) : '–'}</span></span>)}
      </div>
      {lines.some(s => s.pts.length) ? (
        <svg width={width} height={h} aria-hidden className="block">
          <line x1="2" x2={width - 38} y1={y(0)} y2={y(0)} stroke="#262626" />
          <line x1="2" x2={width - 38} y1={y(5)} y2={y(5)} stroke="#1f1f1f" />
          {lines.map(s => segments(s.pts, 7).map((seg, i) => (seg.length > 1
            ? <polyline key={`${s.key}${i}`} fill="none" stroke={s.colour} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" points={seg.map(p => `${x(p.t)},${y(p.v)}`).join(' ')} />
            : <circle key={`${s.key}${i}`} cx={x(seg[0].t)} cy={y(seg[0].v)} r="2.5" fill={s.colour} />)))}
          <text x={width - 34} y={y(0) + 3} fontSize="9" fill="#6b6b6b">0</text>
          <text x={width - 34} y={y(5) + 3} fontSize="9" fill="#6b6b6b">5</text>
        </svg>
      ) : <p className="text-sm text-gray-500 py-3">Log a few days and the trend shows here.</p>}
    </div>
  );
}
